/* eslint-disable react-hooks/exhaustive-deps */
import { gql } from '@apollo/client'
import { breakpointSm } from '@farol-ds/tokens'
import { useViewportSize } from '@jusbrasil-web/shared-hooks'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'

import { PlanFrequency } from '../../../../../constants'
import { useChooseFrequencyLaterEvents } from '../../../../../hooks/use-choose-frequency-later-events'
import { CheckoutButton } from '../../../../checkout-button'
import { useChooseOfferFrequencyLaterContext } from '../../../context-provider'
import { ChooseFrequencyLaterPaymentFooter } from '../../choose-frequency-later-payment-footer'
import { DropdownOffers } from '../../dropdown-offers'
import { FrequencyList } from '../../frequency-list'
import {
  ChooseFrequencyLaterSelectFrequencyStep_OfferLockDataFragment,
  ChooseFrequencyLaterSelectFrequencyStep_ViewerFragment,
} from './__generated__/choose-frequency-later-select-frequency-step.graphql'
import styles from './choose-frequency-later-select-frequency-step.module.scss'

type AvailablePlans =
  ChooseFrequencyLaterSelectFrequencyStep_OfferLockDataFragment['body']['cards'][number]

interface ChooseFrequencyLaterSelectFrequencyStepProps {
  offerLockData: ChooseFrequencyLaterSelectFrequencyStep_OfferLockDataFragment
  viewer: ChooseFrequencyLaterSelectFrequencyStep_ViewerFragment
}

export const ChooseFrequencyLaterSelectFrequencyStep = ({
  offerLockData,
  viewer,
}: ChooseFrequencyLaterSelectFrequencyStepProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const { width } = useViewportSize({
    initialDelay: 0,
    delay: 50,
    initialWidth: window.innerWidth,
    initialHeight: window.innerHeight,
  })

  const {
    metricsRef,
    sendPlanSelectedEvent,
    sendMonthlyFrequencySelectedEvent,
    sendYearlyFrequencySelectedEvent,
    sendStep2ViewEvent,
  } = useChooseFrequencyLaterEvents()
  const {
    isPaywall,
    isCompactModal,
    selectedPlanOffers,
    selectedFrequencyOffer,
    handleSelectPlan,
    handleSelectFrequencyOffer,
    backToSelectOffer,
  } = useChooseOfferFrequencyLaterContext()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const creditCard = selectedFrequencyOffer?.creditCard
  const hasCreditCard = !!creditCard?.ccToken

  const availablePlans = useMemo(() => {
    const cards = offerLockData?.body?.cards || []
    const yearlyPlans = cards.filter((card) => card.offer.frequency === PlanFrequency.YEARLY)
    const monthlyPlans = cards.filter((card) => card.offer.frequency === PlanFrequency.MONTHLY)
    const allPlans = [...yearlyPlans, ...monthlyPlans]

    const uniquePlans = new Map<string, AvailablePlans>()

    allPlans.forEach((card) => {
      if (uniquePlans.has(card.title)) return
      uniquePlans.set(card.title, card)
    })

    return Array.from(uniquePlans.values())
  }, [offerLockData])

  const hasMultiplePlans = availablePlans.length > 1

  const handlePlanChange = (planTitle: string) => {
    const selectedPlanCard = availablePlans.find((plan) => plan.title === planTitle)
    if (!selectedPlanCard) return

    sendPlanSelectedEvent(selectedPlanCard.title, selectedPlanCard)

    handleSelectPlan(selectedPlanCard.title)
    setIsDropdownOpen(false)
  }

  const handleFrequencyChange = (selectedFrequency: string) => {
    const selectedOffer = selectedPlanOffers?.find(
      (planOffer) => planOffer.offer.frequency === selectedFrequency
    )
    if (!selectedOffer) return

    if (selectedFrequency === PlanFrequency.YEARLY) {
      sendYearlyFrequencySelectedEvent(selectedOffer)
    } else {
      sendMonthlyFrequencySelectedEvent(selectedOffer)
    }

    handleSelectFrequencyOffer(selectedOffer)
  }

  useEffect(() => {
    if (selectedPlanOffers?.[0]) sendStep2ViewEvent(selectedPlanOffers[0])
  }, [])

  useEffect(() => {
    const isSmallDevice = width <= breakpointSm
    if (contentRef.current && isSmallDevice)
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  return (
    <div
      className={classNames(styles.container, {
        [styles.containerPaywall]: isPaywall,
      })}
    >
      <div
        ref={contentRef}
        className={classNames(styles.content, {
          [styles.contentPaywall]: isPaywall,
        })}
      >
        {hasMultiplePlans && (
          <div
            className={classNames(styles.selectSection, {
              [styles.selectSectionPaywall]: isPaywall,
            })}
          >
            <DropdownOffers
              isOpen={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
              onPlanChange={handlePlanChange}
              offerLockCard={availablePlans}
              selectedPlanOffer={selectedPlanOffers?.[0]}
            />
          </div>
        )}

        <div
          className={classNames({
            [styles.singlePlanFrequencyList]: !hasMultiplePlans && isCompactModal,
            [styles.paywallFrequencyList]: isPaywall,
          })}
        >
          <FrequencyList
            metricsRef={metricsRef}
            planOffers={selectedPlanOffers}
            defaultFrequency={
              selectedFrequencyOffer?.offer?.frequency === PlanFrequency.YEARLY
                ? PlanFrequency.YEARLY
                : PlanFrequency.MONTHLY
            }
            onFrequencyChange={handleFrequencyChange}
          />
        </div>

        {hasCreditCard && selectedFrequencyOffer && (
          <div className={styles.checkoutButtonContainer}>
            <CheckoutButton offerLockCard={selectedFrequencyOffer} viewer={viewer} />
          </div>
        )}
      </div>

      {selectedFrequencyOffer && (
        <ChooseFrequencyLaterPaymentFooter
          viewer={viewer}
          isPaywall={isPaywall}
          offerLockCard={selectedFrequencyOffer}
          onBackBtnClick={backToSelectOffer}
        />
      )}
    </div>
  )
}

ChooseFrequencyLaterSelectFrequencyStep.fragments = {
  offerLockData: gql`
    fragment ChooseFrequencyLaterSelectFrequencyStep_offerLockData on OfferLockData {
      body {
        cards {
          ...DropdownOffers_offerLockCard
          ...FrequencyList_offerLockCard
          ...CheckoutButton_offerLockCard
          ...ChooseFrequencyLaterPaymentFooter_offerLockCard
        }
      }
    }
    ${DropdownOffers.fragments.offerLockCard}
    ${FrequencyList.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${ChooseFrequencyLaterPaymentFooter.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment ChooseFrequencyLaterSelectFrequencyStep_viewer on Profile {
      ...ChooseFrequencyLaterPaymentFooter_viewer
      ...CheckoutButton_viewer
    }
    ${ChooseFrequencyLaterPaymentFooter.fragments.viewer}
    ${CheckoutButton.fragments.viewer}
  `,
}
